import React, { useEffect, useState, memo } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { getContentLoadStatus } from '../../../../content';
/**
 * LoadedModulesWidget — Boot Flow Refactor diagnostics.
 *
 * Shows which of the 10 content modules are currently loaded into memory
 * and which are still pending. Polls every second so admins can watch
 * idle prefetch progress in real-time.
 */
export function LoadedModulesWidget() {
  const [status, setStatus] = useState(() => getContentLoadStatus());
  // F-A2 (19 May 2026): poll only while the tab is visible. When the admin
  // tabs away (or locks their screen) we stop polling and resume on focus —
  // no point hammering `getContentLoadStatus()` every second on a hidden
  // tab. Audit §13 (perf hygiene).
  useEffect(() => {
    if (status.allLoaded) return;
    let intervalId: number | null = null;
    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        const next = getContentLoadStatus();
        setStatus(next);
        if (next.allLoaded && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    };
    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Catch up immediately on resume so the UI isn't stale for up to 1s.
        const next = getContentLoadStatus();
        setStatus(next);
        if (!next.allLoaded) startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [status.allLoaded]);
  const {
    totalModules,
    loadedModuleCodes,
    pendingModuleCodes,
    allLoaded
  } = status;
  const loadedCount = loadedModuleCodes.length;
  const pct = Math.round(loadedCount / totalModules * 100);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8242" data-mp="ecdrsxv7480v">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-8243" data-mp="ie8yyizt5fmk">
        <div className="flex items-center gap-2" data-id="element-8244" data-mp="3hue2wlw1stip">
          <div className="w-4 h-4 text-gray-500" data-id="element-8245" data-mp="3hvp72mteo3ac" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-8246" data-mp="3mrxhjmwnr8b6">
            Geladen modules
          </h3>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${allLoaded ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-8247" data-mp="3qtj27uffoqvx">
          
          {loadedCount} / {totalModules}
        </span>
      </div>
      <div className="p-4 sm:p-5 space-y-4" data-id="element-8248" data-mp="16664jscnzbf8">
        <div data-id="element-8249" data-mp="1b2d8jfli2cuj">
          <div className="flex justify-between text-xs mb-1.5" data-id="element-8250" data-mp="1b1v4vxwicwcl">
            <span className="text-gray-700 font-medium" data-id="element-8251" data-mp="1l6to07v2hrxt">Voortgang</span>
            <span className="font-semibold text-gray-600" data-id="element-8252" data-mp="1l8xpyk9eogvg">{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-8253" data-mp="1g8s3dihztkk8">
            <div className={`h-2 rounded-full transition-all ${allLoaded ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
            width: `${pct}%`
          }} data-id="element-8254" data-mp="1bg8gtw1bvf3a" />
            
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-8255" data-mp="12heowiyxzcw2">
          {loadedModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-800" data-id="element-8256" data-mp="xh92pibfyild">
            
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-8257" data-mp="sipdn91jwqos" />
              <span className="font-mono font-medium" data-id="element-8258" data-mp="ohvsgnfhtpzl">{code}</span>
            </div>)}
          {pendingModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-500" data-id="element-8259" data-mp="2mvx5cru8cm67">
            
              <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" data-id="element-8260" data-mp="2py1wpn1mlwno" />
              <span className="font-mono font-medium" data-id="element-8261" data-mp="2pyuye9lq9vyy">{code}</span>
            </div>)}
        </div>

        {!allLoaded && <p className="text-xs text-gray-500" data-id="element-8262" data-mp="2uusdkcl98wa5">
            Idle prefetch is bezig op de achtergrond. Modules worden in-place
            aan de indexen toegevoegd zodra ze geladen zijn.
          </p>}
      </div>
    </div>;
}